import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { icons } from '../../../assets/icons';
import './index.scss';
import { useSelector, useDispatch } from 'react-redux';
import { getOrderByIdAdmin, updateOrderByIdAdmin } from '../../../api/redux/slices/Admin';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LoaderComponent } from '../../../helpers/components/loader';
import moment from 'moment';
import { roundToThreeDecimals } from '../../../helpers/modules';
import { ButtonComponent } from '../../../components/buttons';
import { DataErrorModal, showToast } from '../../../components/modal/index';
import { ShopInfo, ClientInfo, SuperAdminInfo } from '../../../components/userInfo/index';

const UpdateOrder = () => {
    const params = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const token = localStorage.getItem('accesstoken');

    const { orderDetails } = useSelector((state) => state.admin);

    const [detailOrder, setDetailOrder] = useState(orderDetails ?? {});
    const [remiseTotal, setRemiseTotal] = useState(0);
    const [updatedList, setUpdatedList] = useState([]);
    const [loadingData, setLoadingData] = useState(false);
    const [loaderAction, setLoaderAction] = useState(false);
    const [updatedQuantities, setUpdatedQuantities] = useState([]);
    const [errorData, setErrorData] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [clientType, setClientType] = useState('detaillant');

    useEffect(() => {
        setRemiseTotal(Math.max(remiseTotal, 0));
    }, [remiseTotal]);

    const stableDispatchOrderById = useCallback(() => {
        setLoadingData(true);
        dispatch(getOrderByIdAdmin({ token, id: params.id }))
            .unwrap()
            .then((res) => {
                const updatedQuantitiesArr = [];
                let holdRemise = res.data.list[0]?.remise || 0;

                const listWithOrderItemId = res.data.list.map((el) => ({
                    ...el,
                    order_item_id: el._id?.toString(),
                    product_id: el.product_id?._id?.toString() || el.product_id,
                    display_price_HT: el.prise_product_HT,
                    display_original_price: el.original_price || (el.prise_product_HT / (1 - (el.remise || 0) / 100)),
                }));

                listWithOrderItemId.forEach((el) => {
                    updatedQuantitiesArr.push(el?.quantity || 0);
                    if (holdRemise !== (el?.remise || 0)) {
                        holdRemise = el?.remise || 0;
                    }
                });

                setDetailOrder(res.data);
                setUpdatedList(listWithOrderItemId);
                setUpdatedQuantities(updatedQuantitiesArr);
                setRemiseTotal(listWithOrderItemId[0]?.remise !== holdRemise ? 0 : holdRemise);
                setClientType(res.data?.order?.clientInfo?.categories || 'detaillant');
            })
            .catch((err) => {
                toast.error(err?.error?.message || 'can not fetch this order', {
                    position: 'top-center',
                    autoClose: 2000,
                });
                setTimeout(() => navigate(`/admin/orders/${params.page}`), 1000);
            })
            .finally(() => setLoadingData(false));
    }, [dispatch, params, token, navigate]);

    useEffect(() => {
        stableDispatchOrderById();
    }, [stableDispatchOrderById, token, params]);

    const calculateTotalTvaAmount = () => {
        let totalTva = 0;
        updatedList?.forEach((el) => {
            if (!el) return;
            const tvaValue = el?.tva ?? 0;
            const priceHT = el?.display_price_HT || 0;
            const quantity = el?.quantity || 0;
            totalTva += priceHT * quantity * (tvaValue / 100);
        });
        return roundToThreeDecimals(totalTva) || 0;
    };

    const calculateTotalTvaByRate = () => {
        const tvaMap = new Map();
        updatedList?.forEach((el) => {
            if (!el) return;
            const tvaValue = el?.tva ?? 0;
            const priceHT = el?.display_price_HT || 0;
            const quantity = el?.quantity || 0;
            const tvaAmount = priceHT * quantity * (tvaValue / 100);
            tvaMap.set(tvaValue, (tvaMap.get(tvaValue) || 0) + tvaAmount);
        });
        return Array.from(tvaMap.entries()).map(([tvaValue, totalAmount]) => ({
            tvaValue,
            totalAmount: roundToThreeDecimals(totalAmount) || 0,
        }));
    };

    const getTotalHT = () => {
        if (!updatedList || updatedList.length === 0) return 0;
        let total = 0;
        updatedList.forEach((el) => {
            const priceHT = el?.display_price_HT || 0;
            const quantity = el?.quantity || 0;
            total += priceHT * quantity;
        });
        return roundToThreeDecimals(total);
    };

    const TotalTCC = () => {
        const totalTvaSum = calculateTotalTvaAmount();
        const totalHTAmount = getTotalHT();
        const timbreAmount = detailOrder?.order?.timbre || 0;
        return roundToThreeDecimals(totalTvaSum + totalHTAmount + timbreAmount);
    };

    const renderTvaValues = () => {
        return calculateTotalTvaByRate()?.map(({ tvaValue, totalAmount }, index) => (
            <div key={index}>
                <h1>{`TVA (${tvaValue}%)`}</h1>
                <span id='somme-totale'>{totalAmount || 0} DT</span>
            </div>
        ));
    };

    const handleUpdateOrder = (e) => {
        e.preventDefault();
        if (updatedList.some((item) => !item.quantity || item.quantity === 0)) {
            showToast('La quantité minimale doit être de 1', 'warn');
            return;
        }
        setLoaderAction(true);

        const productList = updatedList.map((item) => ({
            order_item_id: item?.order_item_id,
            product_id: item?.product_id,
            quantity: item?.quantity || 0,
            remise: item?.remise ?? 0,
        }));

        const data = { list: productList };

        dispatch(updateOrderByIdAdmin({ token, id: params.id, data }))
            .unwrap()
            .then((res) => {
                toast.success(res.message, { position: 'top-center', autoClose: 1000 });
                setTimeout(() => navigate(`/admin/orders/${params.page}`), 1000);
            })
            .catch((err) => {
                if (err?.data) {
                    setIsOpen(true);
                    setErrorData(err.data.map((item) => ({
                        Designation: item.designation,
                        error: `Stock insuffisant, maximum disponible : ${item.stock}`,
                    })));
                } else {
                    toast.error(err?.message || 'Une erreur est survenue', {
                        position: 'top-center',
                        autoClose: 1000,
                    });
                }
            })
            .finally(() => setLoaderAction(false));
    };

    const handleChangeProductRemise = (index, e) => {
        const value = Math.min(Math.max(Number(e.target.value) || 0, 0), 100);
        setUpdatedList((prevList) => {
            const copy = [...prevList];
            const oldPrice = copy[index].display_original_price || copy[index].display_price_HT || 0;
            const newPriceAfterRemise = oldPrice * (1 - value / 100);
            copy[index] = { 
                ...copy[index], 
                remise: value,
                display_price_HT: newPriceAfterRemise
            };
            return copy;
        });
    };

    const handleChangeProductQuantity = (index, e) => {
        const newQuantity = Math.max(Number(e.target.value) || 1, 1);
        setUpdatedQuantities((prev) => {
            const copy = [...prev];
            copy[index] = newQuantity;
            return copy;
        });
        setUpdatedList((prevList) => {
            const copy = [...prevList];
            copy[index] = { ...copy[index], quantity: newQuantity };
            return copy;
        });
    };

    const handleDispatchRemiseTotal = (e) => {
        const value = Math.min(Math.max(Number(e.target.value) || 0, 0), 100);
        setRemiseTotal(value);
        setUpdatedList((prevList) => prevList.map((el) => {
            const oldPrice = el.display_original_price || el.display_price_HT || 0;
            const newPriceAfterRemise = oldPrice * (1 - value / 100);
            return { 
                ...el, 
                remise: value,
                display_price_HT: newPriceAfterRemise
            };
        }));
    };

    return (
        <div id='updateOrder'>
            {isOpen && (
                <DataErrorModal isOpen={isOpen} closeModal={() => setIsOpen(false)} errorData={errorData} />
            )}
            <div className='update-order-header'>
                <Link to={`/admin/orders/${params.page}`}>
                    <icons.BsArrowLeft style={{ color: '#5B5B5B', marginRight: '10px' }} />
                    <h1>Bons de commandes</h1>
                </Link>
                <div style={{ display: 'flex', gap: '1rem' }}>
                    <Link to={`/admin/orders/${params.page}`}>
                        <button className='button-nature'>Annuler</button>
                    </Link>
                    <ButtonComponent
                        name={'Enregistrer'}
                        className={'button-primary'}
                        loading={loaderAction}
                        type={'button'}
                        onClick={handleUpdateOrder}
                    />
                </div>
            </div>
            {loadingData ? (
                <LoaderComponent />
            ) : (
                <div className='container'>
                    <form>
                        <div className='credentials'>
                            <div className='infos-societe left' style={{ border: 'none' }}>
                                <label style={{ textTransform: 'uppercase' }}>
                                    🛒 Jeunesse
                                </label>
                            </div>
                            <div className='left'>
                                <div className='total'>
                                    <label>Montant Reçu</label>
                                    <span>
                                        {roundToThreeDecimals(detailOrder?.order?.clientInfo?.received_amount) || '0'} DT
                                    </span>
                                </div>
                                <div className='total'>
                                    <label>Montant Dépensé</label>
                                    <span>
                                        {roundToThreeDecimals(detailOrder?.order?.clientInfo?.spent_amount) || '0'} DT
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className='credentials'>
                            <div className='left'>
                                <div className='left-el'>
                                    <label>Numéro</label>
                                    <span>{detailOrder?.order?.reference ?? '-'}</span>
                                </div>
                                <div className='left-el'>
                                    <label>Date</label>
                                    <span>
                                        {detailOrder?.order?.date
                                            ? moment(detailOrder.order.date).format('DD-MM-YYYY')
                                            : '-'}
                                    </span>
                                </div>
                                <div className='left-el'>
                                    <label>Référence code client</label>
                                    <span>{detailOrder?.order?.clientInfo?.customs_code || '-'}</span>
                                </div>
                            </div>
                            <div className='right'>
                                <ClientInfo clientInfo={detailOrder?.order?.clientInfo} />
                                <span>
                                    <ShopInfo shop={detailOrder?.order?.shopInfo} />
                                </span>
                            </div>
                        </div>
                        <div className='devis'>
                            <div className='devis-head'>
                                <h1 id='devis-name-order-admin'>Devis</h1>
                                <div className='total-devis'>
                                    <div>
                                        <div className='total-devis-line'>
                                            <h1>TYPE DE CLIENT</h1>
                                            <div className='info-dropdown'>
                                                <select
                                                    value={clientType}
                                                    disabled
                                                    style={{ opacity: 0.7, cursor: 'not-allowed' }}
                                                >
                                                    <option value='grossiste'>Grossiste</option>
                                                    <option value='detaillant'>Detaillant</option>
                                                    <option value='monoprix'>Grande surface</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className='total-devis-line'>
                                            <h1>APPLIQUÉ LA REMISE À TOUS (%)</h1>
                                            <input
                                                type='number'
                                                value={remiseTotal || 0}
                                                onChange={handleDispatchRemiseTotal}
                                                min={0}
                                                max={100}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <p>
                                    Cher Client,
                                    <br />
                                    Nous avons bien reçu votre demande de devis et nous vous en remercions.
                                    <br />
                                    Nous vous prions de trouver ci-dessous nos conditions les meilleures.
                                </p>
                            </div>
                            <div className='devis-table'>
                              <div className='table-scroll-wrapper'>
                                <table className='offres'>
                                    <thead>
                                        <tr id='titres'>
                                            <th>RÉFÉRENCE</th>
                                            <th>RÉFÉRENCE CLT</th>
                                            <th>DÉSIGNATION</th>
                                            <th>QTÉ</th>
                                            <th>PU HT</th>
                                            <th>REMISE (%)</th>
                                            <th>TVA (%)</th>
                                            <th>MONTANT HT</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {updatedList?.map((el, index) => {
                                            const priceHT = el?.display_price_HT || 0;
                                            const tvaRate = el?.tva ?? 0;
                                            const montantHT = priceHT * (el?.quantity || 0);
                                            return (
                                                <tr className='elements' key={index}>
                                                    <td>{el?.reference || '-'}</td>
                                                    <td>{el?.refranceCTL || '-'}</td>
                                                    <td>{el?.designation || '-'}</td>
                                                    <td>
                                                        <input
                                                            type='number'
                                                            name='quantity'
                                                            min={1}
                                                            value={updatedQuantities?.[index] ?? el?.quantity ?? 1}
                                                            onWheel={(e) => e.currentTarget.blur()}
                                                            onChange={(e) =>
                                                                handleChangeProductQuantity(index, e)
                                                            }
                                                        />
                                                    </td>
                                                    <td>{roundToThreeDecimals(priceHT)} DT</td>
                                                    <td>
                                                        <input
                                                            type='number'
                                                            name='remise'
                                                            min={0}
                                                            max={100}
                                                            value={el?.remise ?? 0}
                                                            onWheel={(e) => e.currentTarget.blur()}
                                                            onChange={(e) =>
                                                                handleChangeProductRemise(index, e)
                                                            }
                                                        />
                                                    </td>
                                                    <td>{tvaRate}%</td>
                                                    <td>{roundToThreeDecimals(montantHT)} DT</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                </div>
                                <div className='total-devis-table'>
                                    <div id='box'>
                                        <div>
                                            <h1>TOTAL HT</h1>
                                            <span id='somme-totale'>{getTotalHT()} DT</span>
                                        </div>
                                        {renderTvaValues()}
                                        <div>
                                            <h1>TIMBRE</h1>
                                            <span id='somme-totale'>
                                                {detailOrder?.order?.timbre || 0} DT
                                            </span>
                                        </div>
                                        <div>
                                            <h1>TOTAL TTC</h1>
                                            <span id='somme-totale'>{TotalTCC()} DT</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='message'>
                                <p>
                                    Nous sommes à votre disposition pour tout complément d'informations.{' '}
                                    <br />
                                    Nous vous prions d'agréer, Cher Client, nos sincères salutations.{' '}
                                    <br />
                                    Le service commercial.
                                </p>
                            </div>
                            <SuperAdminInfo superAdminInfo={[detailOrder?.order?.companyInfo]} />
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default UpdateOrder;